export enum FormType {
  YACHT             = 'YACHT',
  BOAT              = 'BOAT',
  WATER_TOYS        = 'WATER_TOYS',
  ENGINE_GENERATOR  = 'ENGINE_GENERATOR',
  PARTS_ACCESSORIES = 'PARTS_ACCESSORIES',
  SERVICES          = 'SERVICES',
  SKILL_LABOUR      = 'SKILL_LABOUR',
  CHARTER           = 'CHARTER',
}

export const FORM_TYPE_LABELS: Record<FormType, string> = {
  [FormType.YACHT]:             'Yacht',
  [FormType.BOAT]:              'Boat',
  [FormType.WATER_TOYS]:        'Water Toys',
  [FormType.ENGINE_GENERATOR]:  'Engine / Generator',
  [FormType.PARTS_ACCESSORIES]: 'Parts & Accessories',
  [FormType.SERVICES]:          'Services',
  [FormType.SKILL_LABOUR]:      'Skill Labour',
  [FormType.CHARTER]:           'Charter',
};

export enum InputType {
  TEXTBOX                   = 'TEXTBOX',
  NUMERIC_TEXTBOX           = 'NUMERIC_TEXTBOX',
  TEXTAREA                  = 'TEXTAREA',
  DROPDOWN                  = 'DROPDOWN',
  SELECTABLE_PILL           = 'SELECTABLE_PILL',
  MULTI_SELECTABLE_PILL     = 'MULTI_SELECTABLE_PILL',
  MULTI_DROPDOWN            = 'MULTI_DROPDOWN',
  TOGGLE                    = 'TOGGLE',
  TOGGLE_SPECIAL            = 'TOGGLE_SPECIAL',
  BUTTON                    = 'BUTTON',
  IMAGE_VIDEO_UPLOADER      = 'IMAGE_VIDEO_UPLOADER',
  IMAGE_UPLOADER            = 'IMAGE_UPLOADER',
  FILE_UPLOADER             = 'FILE_UPLOADER',
  SEARCH_LOCATION           = 'SEARCH_LOCATION',
  SEARCH_COUNTRY            = 'SEARCH_COUNTRY',
  SPEC_DYNAMIC              = 'SPEC_DYNAMIC',
  DURATION_DYNAMIC          = 'DURATION_DYNAMIC',
  DYNAMIC_TEXTBOX           = 'DYNAMIC_TEXTBOX',
  DYNAMIC_CHECK_TEXTBOX     = 'DYNAMIC_CHECK_TEXTBOX',
  PRICE_CALENDAR            = 'PRICE_CALENDAR',
  PROMOTIONS_DYNAMIC        = 'PROMOTIONS_DYNAMIC',
  OPTIONAL_EXTRAS_DYNAMIC   = 'OPTIONAL_EXTRAS_DYNAMIC',
  AI_BUTTON                 = 'AI_BUTTON',
  PICKUP_POINTS             = 'PICKUP_POINTS',
  PICKUP_LOCATION           = 'PICKUP_LOCATION',
  DATE_PICKER               = 'DATE_PICKER',
}

// ── Translation types ──────────────────────────────────────────────────────

/** Per-locale label map for options. { "en": "Sail Yacht", "zh-Hant": "帆船" } */
export type OptionTranslations = Record<string, string>;

/** Per-locale display strings for a field. */
export type FieldTranslations = Record<string, { label?: string; placeholder?: string; suffix?: string; remark?: string }>;

/** Per-locale step title. { "en": "Basic Info", "zh-Hant": "基本資訊" } */
export type StepTranslations = Record<string, string>;

// ── Data types ─────────────────────────────────────────────────────────────

export interface FieldOption {
  id?: string;
  value: string;
  label: string;
  sortOrder?: number;
  translations?: OptionTranslations;
}

export interface FormStepDef {
  id?: string;
  stepNumber: number;
  sortOrder: number;
  translations: StepTranslations;
}

export interface FormFieldDef {
  id?: string;
  key: string;
  isActive?: boolean;
  icon?: string | null;
  remark?: string | null;
  /** Hint for the AI assistant only — never shown in the app. */
  aiHint?: string | null;
  step: number;
  order: number;
  group?: string | null;
  /** FK to form_config_field_groups.id — populated by the API, read-only in the CMS. */
  groupId?: string;
  label: string;
  inputType: InputType;
  required: boolean;
  visible: boolean;
  placeholder?: string | null;
  /** Unit suffix shown after/inside the input, e.g. "ft" (EN base value). */
  suffix?: string | null;
  /** Fields sharing a unitPairKey express one quantity in different units.
   *  The member with unitFactor === 1 is the base unit. */
  unitPairKey?: string | null;
  unitFactor?: number | null;
  unitDecimals?: number | null;
  options?: FieldOption[];
  translations?: FieldTranslations;
  maxLength?: number | null;
  minValue?: number;
  maxValue?: number;
  minPhotos?: number | null;
  isFilterable: boolean;
  isSortable: boolean;
  isSearchable: boolean;
  isListFilter: boolean;
  allowMultiple: boolean;
  listingSection?: string | null;
  detailSection?: string | null;
  layoutFixed: boolean;
  detailFixed: boolean;
  /**
   * For AI_BUTTON fields: keys of sibling fields whose values will be
   * sent to the AI description endpoint when the button is tapped.
   */
  linkedFields?: string[];
  /**
   * For AI_BUTTON fields: key of the sibling field that receives the
   * AI-generated text after the button is tapped.
   */
  targetField?: string | null;
  /**
   * camelCase column on the listings table this field maps to.
   * Read-only — set by seed/backend. Displayed as a badge in the CMS.
   */
  listingColumn?: string;
  /** For condition fields: preset uses New Build / Pre-Owned. */
  isBuildCondition?: boolean;
  /** For condition fields: preset uses New / Used / Refurbished. */
  isStatusCondition?: boolean;
  /**
   * Conditional visibility rule in "fieldKey=value" format.
   * Field is hidden unless the named sibling field equals the given value.
   * Example: "booking_type=Hourly"
   * `null` is sent on save to explicitly clear the rule (survives JSON, unlike `undefined`).
   */
  visibleWhen?: string | null;
}

export const FORM_TYPE_NAV_SECTION: Record<FormType, string> = {
  [FormType.YACHT]:             'buy',
  [FormType.BOAT]:              'buy',
  [FormType.WATER_TOYS]:        'buy',
  [FormType.ENGINE_GENERATOR]:  'buy',
  [FormType.CHARTER]:           'charter',
  [FormType.PARTS_ACCESSORIES]: 'parts',
  [FormType.SERVICES]:          'labour',
  [FormType.SKILL_LABOUR]:      'labour',
};

export const NAV_SECTION_LABELS: Record<string, { label: string; emoji: string }> = {
  buy:     { label: 'Buy',     emoji: '🛒' },
  charter: { label: 'Charter', emoji: '🗓️' },
  parts:   { label: 'Parts',   emoji: '🔧' },
  labour:  { label: 'Labour',  emoji: '👷' },
};

export const NAV_SECTION_ORDER = ['buy', 'charter', 'parts', 'labour'];

export interface FormGroupDef {
  id?: string;
  name: string;
  allowMultiple: boolean;
  maxInstances?: number | null;
}

export interface FormConfig {
  id: string;
  formType: FormType;
  version: number;
  isActive: boolean;
  icon?: string;
  remark?: string;
  listingCost: number;
  visibleDays: number;
  listingCategoryId?: string | null;
  listingCategory?: { id: string; key: string } | null;
  fields: FormFieldDef[];
  steps: FormStepDef[];
  fieldGroups: FormGroupDef[];
  updatedAt: string;
}

// ── Section options ────────────────────────────────────────────────────────

export interface FormConfigSectionOption {
  id: string;
  sectionType: 'listing' | 'detail';
  value: string;
  sortOrder: number;
  createdAt: string;
}

export interface FormConfigSectionOptions {
  listing: FormConfigSectionOption[];
  detail: FormConfigSectionOption[];
}

/** Lightweight shape returned by GET /admin/form-configs (list page only). */
export interface FormConfigSummary {
  id: string;
  formType: FormType;
  version: number;
  isActive: boolean;
  fieldCount: number;
  updatedAt: string;
  listingCategoryId?: string | null;
}

export interface FormPricingRow {
  id: string;
  formType: FormType;
  listingCost: number;
  visibleDays: number;
}

export interface AddonConfigItem {
  cost: number;
  days: number;
}

export interface AddonConfig {
  boost: AddonConfigItem;
  topOfPage: AddonConfigItem;
  whatsapp: AddonConfigItem;
  popupAd: AddonConfigItem;
}

export interface FormPricingResponse {
  formConfigs: FormPricingRow[];
  addonConfig: AddonConfig;
}
